import React from 'react';
import { ArrowLeft, Hash, AlertTriangle, Pin, FileText, Image, Table, Info, Lightbulb, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TagsDocumentation = () => {
  const navigate = useNavigate();

  const backendTags = [
    {
      category: "Structure du Document",
      tags: [
        {
          name: "! Titre Thématique",
          syntax: "! Titre de la thématique",
          description: "Crée une nouvelle thématique (groupe de chapitres)",
          example: "! Introduction à la sécurité"
        },
        {
          name: "! /",
          syntax: "! /",
          description: "Ferme la thématique courante",
          example: "! /"
        },
        {
          name: "# Chapitre",
          syntax: "# Titre du chapitre",
          description: "Crée un nouveau chapitre",
          example: "# Chapitre 1: Les bases"
        },
        {
          name: "## Section",
          syntax: "## Titre de la section",
          description: "Crée une nouvelle section dans le chapitre courant",
          example: "## 1.1 Introduction"
        },
        {
          name: "### Sous-section",
          syntax: "### Titre de la sous-section",
          description: "Crée une nouvelle sous-section dans la section courante",
          example: "### 1.1.1 Définitions"
        }
      ]
    },
    {
      category: "Contrôle du Contenu",
      tags: [
        {
          name: "!! Ignorer",
          syntax: "!! texte à ignorer",
          description: "Ignore le contenu suivant jusqu'à la balise de fermeture",
          example: "!! Ce texte ne sera pas extrait"
        },
        {
          name: "!! /",
          syntax: "!! /",
          description: "Arrête le mode d'ignorance du contenu",
          example: "!! /"
        }
      ]
    },
    {
      category: "Capture de Régions",
      tags: [
        {
          name: "!!! Table",
          syntax: "!!!",
          description: "Marque le début d'une région spéciale (utilisé en interne)",
          example: "!!!"
        },
        {
          name: "!!! /",
          syntax: "!!! /",
          description: "Marque la fin d'une région spéciale",
          example: "!!! /"
        }
      ]
    },
    {
      category: "Balises XML pour Assets",
      tags: [
        {
          name: "<table>",
          syntax: "<table>\nContenu du tableau\n</table>",
          description: "Délimite une région de tableau à extraire",
          example: "<table>\n| Col1 | Col2 |\n| A | B |\n</table>"
        },
        {
          name: "<img>",
          syntax: "<img>\nZone d'image\n</img>",
          description: "Délimite une région d'image à extraire",
          example: "<img>\nFigure 1: Diagramme\n</img>"
        }
      ]
    },
    {
      category: "Légendes (Détection Automatique)",
      tags: [
        {
          name: "Figure",
          syntax: "Figure N: Titre",
          description: "Détecté automatiquement comme légende d'image",
          example: "Figure 1: Schéma du système"
        },
        {
          name: "Tableau",
          syntax: "Tableau N: Titre",
          description: "Détecté automatiquement comme légende de tableau",
          example: "Tableau 1: Résultats des tests"
        },
        {
          name: "*Légende*",
          syntax: "*Texte entre astérisques*",
          description: "Texte entre astérisques considéré comme légende d'image",
          example: "*Vue d'ensemble du processus*"
        }
      ]
    }
  ];

  const frontendTags = [
    {
      category: "Blocs de Mise en Évidence",
      icon: <Pin size={20} className="text-blue-500" />,
      tags: [
        {
          name: "<note>",
          syntax: "<note>\nContenu de la note\n</note>",
          description: "Affiche un bloc bleu avec icône Pin pour les notes importantes",
          example: "<note>\nLOLER does not apply to lifting equipment which is not provided for use at work.\n</note>",
          preview: (
            <div className="inline-flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              <Pin size={16} className="mt-0.5 text-blue-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="leading-relaxed">Exemple de note importante</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      category: "Avertissements",
      icon: <AlertTriangle size={20} className="text-red-500" />,
      tags: [
        {
          name: "<warning>",
          syntax: "<warning>\nContenu de l'avertissement\n</warning>",
          description: "Affiche un bloc rouge avec icône AlertTriangle pour les avertissements",
          example: "<warning>\nNever exceed the maximum load limit!\n</warning>",
          preview: (
            <div className="inline-flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle size={16} className="mt-0.5 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="leading-relaxed">Exemple d'avertissement</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      category: "Rappels",
      icon: <Pin size={20} className="text-amber-500" />,
      tags: [
        {
          name: "<reminder>",
          syntax: "<reminder>\nContenu du rappel\n</reminder>",
          description: "Affiche un bloc orange avec icône Pin pour les rappels",
          example: "<reminder>\nCheck all equipment before each use.\n</reminder>",
          preview: (
            <div className="inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              <Pin size={16} className="mt-0.5 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="leading-relaxed">Exemple de rappel</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      category: "Articles",
      icon: <FileText size={20} className="text-yellow-600" />,
      tags: [
        {
          name: "<article>",
          syntax: "<article>\nContenu de l'article\n</article>",
          description: "Affiche un bloc jaune pour les articles ou encadrés spéciaux",
          example: "<article>\nCeci est un encadré informatif important.\n</article>",
          preview: (
            <div className="border border-yellow-400 bg-yellow-50 rounded-md px-4 py-3 text-sm text-gray-800">
              <p className="leading-relaxed">Exemple d'article ou encadré</p>
            </div>
          )
        }
      ]
    },
    {
      category: "Informations Importantes",
      icon: <Info size={20} className="text-purple-500" />,
      tags: [
        {
          name: "<important>",
          syntax: "<important>\nContenu important\n</important>",
          description: "Affiche un bloc violet avec icône Info pour les informations cruciales",
          example: "<important>\nCette procédure est obligatoire pour tous les opérateurs.\n</important>",
          preview: (
            <div className="inline-flex items-start gap-2 rounded-md border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-700">
              <Info size={16} className="mt-0.5 text-purple-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="leading-relaxed">Exemple d'information importante</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      category: "Conseils et Astuces",
      icon: <Lightbulb size={20} className="text-green-500" />,
      tags: [
        {
          name: "<tip>",
          syntax: "<tip>\nContenu du conseil\n</tip>",
          description: "Affiche un bloc vert avec icône Lightbulb pour les conseils pratiques",
          example: "<tip>\nUtilisez toujours des gants de protection lors de cette opération.\n</tip>",
          preview: (
            <div className="inline-flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <Lightbulb size={16} className="mt-0.5 text-green-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="leading-relaxed">Exemple de conseil pratique</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      category: "Exemples",
      icon: <Code size={20} className="text-gray-600" />,
      tags: [
        {
          name: "<example>",
          syntax: "<example>\nContenu de l'exemple\n</example>",
          description: "Affiche un bloc gris avec icône Code pour les exemples (police monospace)",
          example: "<example>\nCalcul de charge: 500kg × 1.5 = 750kg\n</example>",
          preview: (
            <div className="inline-flex items-start gap-2 rounded-md border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-800 font-mono">
              <Code size={16} className="mt-0.5 text-gray-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="leading-relaxed">Exemple de calcul ou code</p>
              </div>
            </div>
          )
        }
      ]
    }
  ];

  const inlineFormatting = [
    {
      name: "<gras>",
      syntax: "<gras>texte en gras</gras>",
      description: "Met le texte en gras (bold)",
      example: "Ceci est <gras>très important</gras> à retenir.",
      preview: (
        <span>Ceci est <strong>très important</strong> à retenir.</span>
      )
    },
    {
      name: "<i>",
      syntax: "<i>texte en italique</i>",
      description: "Met le texte en italique (emphasis)",
      example: "Le mot <i>safety</i> signifie sécurité.",
      preview: (
        <span>Le mot <em>safety</em> signifie sécurité.</span>
      )
    },
    {
      name: "<grasi>",
      syntax: "<grasi>texte gras et italique</grasi>",
      description: "Met le texte en gras ET italique",
      example: "Attention : <grasi>zone dangereuse</grasi> !",
      preview: (
        <span>Attention : <strong><em>zone dangereuse</em></strong> !</span>
      )
    }
  ];

  const inlineKeywords = [
    {
      keyword: "WARNING:",
      description: "Ligne COMMENÇANT par 'WARNING:' → texte rouge avec icône. Le mot 'warning' au milieu d'une phrase n'est PAS détecté.",
      preview: (
        <span className="inline-flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700">
          <AlertTriangle size={14} className="mt-0.5 text-red-500" />
          <span className="font-semibold">WARNING: Exemple</span>
        </span>
      )
    },
    {
      keyword: "N'oubliez pas",
      description: "Ligne COMMENÇANT par \"N'oubliez pas\" → texte rouge avec icône",
      preview: (
        <span className="inline-flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700">
          <AlertTriangle size={14} className="mt-0.5 text-red-500" />
          <span className="font-semibold">N'oubliez pas de vérifier</span>
        </span>
      )
    },
    {
      keyword: "REMINDER: / REMEMBER:",
      description: "Ligne COMMENÇANT par 'REMINDER:' ou 'REMEMBER:' → texte orange avec icône",
      preview: (
        <span className="inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-sm text-amber-700">
          <Pin size={14} className="mt-0.5 text-amber-500" />
          <span className="font-semibold">REMINDER: Exemple</span>
        </span>
      )
    },
    {
      keyword: "Rappelle:",
      description: "Ligne COMMENÇANT par 'Rappelle:' → texte orange avec icône",
      preview: (
        <span className="inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-sm text-amber-700">
          <Pin size={14} className="mt-0.5 text-amber-500" />
          <span className="font-semibold">Rappelle: Exemple</span>
        </span>
      )
    },
    {
      keyword: "Note: / Remarque:",
      description: "Ligne COMMENÇANT par 'Note:' ou 'Remarque:' → texte bleu avec icône",
      preview: (
        <span className="inline-flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-700">
          <Pin size={14} className="mt-0.5 text-blue-500" />
          <span className="font-semibold">Note: Exemple</span>
        </span>
      )
    },
    {
      keyword: "~ Note: / ~ Remarque:",
      description: "Ligne commençant par '~ Note:' ou '~ Remarque:' → texte bleu avec icône",
      preview: (
        <span className="inline-flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-700">
          <Pin size={14} className="mt-0.5 text-blue-500" />
          <span className="font-semibold">~ Note: Exemple</span>
        </span>
      )
    }
  ];

  const escapeTags = [
    {
      name: "<text>",
      syntax: "<text>\nContenu à ne pas interpréter\n</text>",
      description: "Échappe le contenu pour qu'il soit affiché comme texte brut, sans détection des mots-clés (warning, note, etc.)",
      example: "<text>\nCeci contient le mot warning mais ne sera pas stylisé en rouge.\n</text>",
      preview: (
        <p className="text-gray-700 leading-relaxed">Ceci contient le mot warning mais ne sera pas stylisé en rouge.</p>
      )
    },
    {
      name: "<raw>",
      syntax: "<raw>\nContenu brut\n</raw>",
      description: "Alias de &lt;text&gt;. Affiche le contenu sans aucun formatage ni interprétation des balises internes.",
      example: "<raw>\nTexte avec <gras>balises</gras> qui ne seront pas interprétées.\n</raw>",
      preview: (
        <p className="text-gray-700 leading-relaxed">Texte avec &lt;gras&gt;balises&lt;/gras&gt; qui ne seront pas interprétées.</p>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/documents')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Retour aux documents"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Documentation des Balises</h1>
              <p className="text-sm text-gray-500 mt-1">Guide complet des balises utilisables dans DigitalBook</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Backend Tags Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Hash size={28} className="text-primary" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Balises Backend (Extraction PDF)</h2>
              <p className="text-sm text-gray-600">Balises utilisées lors de l'upload du PDF pour structurer le contenu</p>
            </div>
          </div>

          {backendTags.map((category, idx) => (
            <div key={idx} className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">{category.category}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {category.tags.map((tag, tagIdx) => (
                  <div key={tagIdx} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-gray-900 mb-1">{tag.name}</h4>
                        <p className="text-sm text-gray-600">{tag.description}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Syntaxe</span>
                        <pre className="mt-1 bg-gray-900 text-gray-100 px-4 py-2 rounded-md text-sm overflow-x-auto">
                          {tag.syntax}
                        </pre>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exemple</span>
                        <pre className="mt-1 bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm overflow-x-auto border border-gray-200">
                          {tag.example}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Frontend Tags Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText size={28} className="text-primary" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Balises Frontend (Affichage)</h2>
              <p className="text-sm text-gray-600">Balises utilisables dans le contenu pour styliser l'affichage</p>
            </div>
          </div>

          {frontendTags.map((category, idx) => (
            <div key={idx} className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-blue-25 px-6 py-4 border-b flex items-center gap-3">
                {category.icon}
                <h3 className="text-lg font-semibold text-gray-900">{category.category}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {category.tags.map((tag, tagIdx) => (
                  <div key={tagIdx} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-gray-900 mb-1">{tag.name}</h4>
                        <p className="text-sm text-gray-600">{tag.description}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Syntaxe</span>
                        <pre className="mt-1 bg-gray-900 text-gray-100 px-4 py-2 rounded-md text-sm overflow-x-auto whitespace-pre-wrap">
                          {tag.syntax}
                        </pre>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exemple</span>
                        <pre className="mt-1 bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm overflow-x-auto border border-gray-200 whitespace-pre-wrap">
                          {tag.example}
                        </pre>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aperçu</span>
                        <div className="mt-2">
                          {tag.preview}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Inline Formatting Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText size={28} className="text-primary" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Formatage Inline</h2>
              <p className="text-sm text-gray-600">Balises pour formater du texte à l'intérieur d'un paragraphe</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {inlineFormatting.map((item, idx) => (
                <div key={idx} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="mb-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">{item.name}</h4>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Syntaxe</span>
                      <pre className="mt-1 bg-gray-900 text-gray-100 px-4 py-2 rounded-md text-sm overflow-x-auto">
                        {item.syntax}
                      </pre>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exemple</span>
                      <pre className="mt-1 bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm overflow-x-auto border border-gray-200">
                        {item.example}
                      </pre>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aperçu</span>
                      <div className="mt-2 text-base">
                        {item.preview}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Inline Keywords Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle size={28} className="text-primary" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Mots-clés Inline (Détection Automatique)</h2>
              <p className="text-sm text-gray-600">Mots-clés détectés automatiquement dans le texte et stylisés</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {inlineKeywords.map((item, idx) => (
                <div key={idx} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="mb-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">{item.keyword}</h4>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aperçu</span>
                    <div className="mt-2">
                      {item.preview}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Escape Tags Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Code size={28} className="text-primary" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Balises d'Échappement</h2>
              <p className="text-sm text-gray-600">Balises pour afficher du texte brut sans interprétation des mots-clés</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {escapeTags.map((tag, idx) => (
                <div key={idx} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="mb-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">{tag.name}</h4>
                    <p className="text-sm text-gray-600">{tag.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Syntaxe</span>
                      <pre className="mt-1 bg-gray-900 text-gray-100 px-4 py-2 rounded-md text-sm overflow-x-auto whitespace-pre-wrap">
                        {tag.syntax}
                      </pre>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exemple</span>
                      <pre className="mt-1 bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm overflow-x-auto border border-gray-200 whitespace-pre-wrap">
                        {tag.example}
                      </pre>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aperçu</span>
                      <div className="mt-2">
                        {tag.preview}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Pin size={20} className="text-blue-600" />
            Conseils d'utilisation
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Les balises backend doivent être ajoutées <strong>dans le PDF source</strong> avant l'upload</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Les balises frontend peuvent être ajoutées <strong>en éditant le contenu</strong> directement dans l'interface</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Les balises sont <strong>masquées à l'affichage</strong>, seul le contenu stylisé apparaît</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Les mots-clés inline sont détectés <strong>automatiquement au début de la ligne</strong> uniquement</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Tous les mots-clés (WARNING:, NOTE:, REMINDER:, etc.) doivent être <strong>au début de la ligne</strong> pour être stylisés</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Utilisez <strong>&lt;text&gt;</strong> ou <strong>&lt;raw&gt;</strong> pour échapper du texte contenant des mots-clés</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TagsDocumentation;
